<template>
	<full-overlay>
		<div v-if="isLoading" style="position: relative;">
			<loading-indicator :is-loading="true" :fullParent="false"/>
		</div>
		<div v-else class="login-box-container">
			<div class="top-control">
				<div class="flex-grow-1"></div>
                <div class="popup-gray-close-button" @click="onClose">
                    <img src="/img/close_cross@2x.png" />
                </div>
			</div>


			<div v-if="logoContentUnit" class="logo"><img :src="context.storageEndpoint + logoContentUnit.file_name" /></div>

			<p class="login-hint text-center" v-if="state === STATE.AUTH_METHOD_SELECTION || state === STATE.PHONE_AUTH" v-html="finalLoginHint"></p>

			<div v-if="state === STATE.AUTH_METHOD_SELECTION" class="login-button-wrapper">
				<!-- Facebook Login -->
				<div v-if="facebookPageToContactCustomer" class="mb-2" style="height: 38px;">
					<iframe ref="fb-iframe" :src="fbAuthIframeUrl" class="border-none" style="width: 220px; height: 38px;"/>
				</div>

				<!-- Line Login -->
				<button cy-button="line-login" v-if="lineModuleEnabled" class="btn-line-auth mb-2" @click="onLineLogin">
					{{$t('line_login')}}
				</button>

				<!-- 手機號碼登入 -->
				<button class="btn-phone-auth" style="max-width: 250px;" @click="state = STATE.PHONE_AUTH">
					{{$t('login_via_phone')}}
				</button>
			</div>

			<div class="mt-4 mb-2">
				<div v-if="shouldShowPhoneInput" class="mx-3">
					<div class="text-left">
						<label class="form-control-label">{{$t('message.phone')}}</label>
					</div>
					<div class="">
						<div class="form-control-static">
							<vue-phone-input
								:disabled="state !== STATE.PHONE_AUTH && state !== STATE.PASSWORD_RESET && state !== STATE.FILL_CUSTOMER_DATA"
								ref="phone-input"
								name="phone"
								:code="countryCode"
								:value="phoneNumber"
								:onChange="onPhoneNumberChange"
								:placeholder="$t('message.login_phone_placeholder')"
								@blur="checkExistingMemberByPhoneNumber"
							/>
							<div
								v-if="(!this.hasValidPhoneNumber || !this.hasValidPhoneCallingCode) && showInvalid"
								cy-errors="phone_number"
								class="text-danger">
								{{$t('message.please_fill_your_phone')}}</div>
						</div>
					</div>
					<div v-if="(state === STATE.PHONE_AUTH || state === STATE.FILL_PASSWORD_FOR_LOGIN) && (this.hasValidPhoneNumber && this.hasValidPhoneCallingCode)" class="text-right">
						<a cy-link="reset-password" @click="requestResetPassword">{{$t('reset_password')}}</a>
					</div>
				</div>
				<div class="mx-3">
					<div v-if="customerDataRequired"  class="mb-2 mt-2">
						<div class="text-left">
							<label class="form-control-label">{{$t('message.name')}}</label>
						</div>
						<div class="form-control-static">
							<input
								cy-input="name"
								v-model="name"
								name="name"
								type="text"
								autocomplete="new-name"
								class="form-control"
								:placeholder="existingMember ? existingMember.masked_name : $t('enter_your_name')"
								:disabled="!!existingMember">
							<div v-if="!this.hasValidName && showInvalid" cy-errors="name" class="text-danger">
								{{$t('message.please_fill_your_name')}}
							</div>
						</div>
					</div>
					<div v-if="customerDataRequired"  class="mb-2">
						<div class="text-left">
							<label class="form-control-label">{{$t('message.email')}}</label>
						</div>
						<div class="form-control-static">
							<input
								cy-input="email"
								v-model="email"
								type="text"
								name="email"
								autocomplete="new-email"
								class="form-control"
								:placeholder="existingMember ? existingMember.masked_email : $t('enter_your_email')"
								:disabled="!!existingMember" >
							<div v-if="!this.hasValidEmail && showInvalid" cy-errors="email" class="text-danger">
								{{$t('message.please_correct_your_email')}}
							</div>
						</div>
					</div>
					<div v-if="state === STATE.FILL_PASSWORD_FOR_RESET && !isRequestingReset" cy-hints="reset" class="text-warning text-center py-2 small-font">
						{{$t('reset_password_required_hint')}}</div>
					<div v-if="state === STATE.FILL_PASSWORD_FOR_RESET && isRequestingReset" cy-hints="reset" class="text-warning text-center py-2 small-font">
						{{$t('request_reset_password_required_hint')}}</div>

					<div class="mb-2">
						<div v-if="passwordRequired">
							<div class="text-left">
								<label class="form-control-label">{{$t('password')}}</label>
							</div>
							<div class="form-control-static">
								<input
									cy-input="password"
									v-model="password"
									type="password"
									name="password"
									autocomplete="new-email"
									:placeholder="$t('password_hint')"
									class="form-control"
								>
							</div>
						</div>
						<div v-if="passwordConfirmationRequired">
							<div class="text-left mt-2">
								<label class="form-control-label">{{$t('password_confirmation')}}</label>
							</div>
							<div class="form-control-static">
								<input
									cy-input="password_confirmation"
									v-model="passwordConfirmation"
									type="password"
									name="password_confirmation"
									autocomplete="new-email"
									:placeholder="$t('password_confirmation_hint')"
									class="form-control"
								>
							</div>
						</div>
						<div v-if="(!hasValidPassword || !hasValidPasswordConfirmation) && showInvalid">
							<div v-if="passwordRequired && !hasValidPassword" cy-errors="password" class="text-danger">
								{{ $t('password_should_be_at_least_6_digits') }}
							</div>
							<div v-if="passwordConfirmationRequired && !hasValidPasswordConfirmation" cy-errors="phone_confirmation" class="text-danger">
								{{ $t('password_confirmation_not_matched') }}
							</div>
						</div>
					</div>
				</div>

				<login-box-sms-verification
					v-if="state === STATE.SMS_VERIFICATION"
					:phone-calling-code="phoneCallingCode"
					:phone-number="phoneNumber"
					:provider="provider"
					@onReachDeadEnd="() => isActionButtonShow = false"
					@change-number="onChangePhoneNumber"
					@code-change="(code) => smsCode = code" 
					@show-contact-info="onShowSmsContactInfo"
				/>

				<div class="text-center mb-4 mt-4" v-show="isActionButtonShow">
					<button v-if="state == STATE.PHONE_AUTH" cy-button="next" @click="checkMemberByPhone" class="btn btn-primary mt-2 btn-sm">
						{{$t('next_step')}}
					</button>
					<button
						v-if="state == STATE.PASSWORD_RESET || state == STATE.FILL_PASSWORD_FOR_RESET || state == STATE.FILL_CUSTOMER_DATA"
						cy-button="go_verify_sms"
						class="btn btn-primary mt-2 btn-sm"
						@click="goToSmsVerification"
					>
						{{$t('verify_sms')}}
					</button>
					<button
						v-if="state == STATE.FILL_PASSWORD_FOR_LOGIN"
						cy-button="login_with_password"
						class="btn btn-primary mt-2 btn-sm"
						@click="loginWithPassword"
					>
						{{$t('login')}}
					</button>
					<button
						v-if="state == STATE.SMS_VERIFICATION"
						cy-button="verify_sms"
						class="btn btn-primary mt-2 btn-sm"
						@click="confirm"
					>
						{{ $t(showSmsContactInfo ? 'confirmed' : 'verify_sms') }}
					</button>
					<div v-if="state == STATE.SMS_VERIFICATION && provider.enable_login_without_sms_verification" class="text-center mt-2">
						<a href="javascript:void(0)" @click="setContinueWithoutSmsCodeThenLogin" class="text-muted d-inline-block">
							<small>
								{{ $t('continue_login_without_sms_verification') }}
							</small>
						</a>
					</div>
					<div class="text-center mt-2">
						<small v-if="continueWithoutSmsVerification">{{ $t('continue_login_without_sms_verification_hint') }}</small>
					</div>
				</div>
			</div>
		</div>
	</full-overlay>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import api from '../mixins/api.js';
import vuePhoneInput from './vue-phone-input/index.vue';
import promptConfirm from '../mixins/prompt-confirm.js';
import CapabilityManager from '../bu/CapabilityManager.js';
import fullOverlay from './common/FullOverlay.vue';
import LoadingIndicator from './common/LoadingIndicator.vue';
import { EventForgetPassword, EventLogin, EventVerifySms } from '../bu/models/GA4.js';
import Cookies from 'js-cookie';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Constants from '../constants.js';

export default {
	components: { vuePhoneInput, fullOverlay, LoadingIndicator },
	mixins: [promptConfirm, api],
	props: {
		loginHint: { type: String },
		logoContentUnit: {type: Object},
		context: {type: Object, required: true},
		provider: {type: Object, required: true}
	},
	beforeCreate() {
		this.STATE = {
			// 選登入方式
			AUTH_METHOD_SELECTION: 'LOGIN_METHOD_SELECTION',

			// 選擇手機登入，輸入手機號碼
			PHONE_AUTH: 'PHONE_AUTH',

			// 選重設密碼，輸入手機號碼
			PASSWORD_RESET: 'PASSWORD_RESET',

			// 重設密碼（查詢後，確實有顧客資料），輸入密碼與密碼確認
			FILL_PASSWORD_FOR_RESET: 'FILL_PASSWORD_FOR_RESET',

			// 輸入密碼以手機＋密碼登入
			FILL_PASSWORD_FOR_LOGIN: 'FILL_PASSWORD_FOR_LOGIN',

			// 沒有顧客資料，需填寫完整表單註冊
			FILL_CUSTOMER_DATA: 'FILL_CUSTOMER_DATA',

			// SMS 驗證階段
			SMS_VERIFICATION: 'SMS_VERIFICATION',

			LOGGED_IN: 'LOGGED_IN'
		}
	},
	data() {
		this.$_capabilityManager = new CapabilityManager(this.provider.grade, this.provider.capabilities);

		var facebookPageToContactCustomer = null;
		if (this.$_capabilityManager.isFBCustomerContactAvailable()) {
			facebookPageToContactCustomer =_.find(this.provider.pages, (p) => {
				return !!p.customer_contact_enabled
			});
		}

		var lineModuleEnabled = false;
		if (this.$_capabilityManager.isLineModuleAvailable()) {
			lineModuleEnabled = !!this.provider.line_integration?.line_login_channel_id && !!this.provider.line_integration?.line_login_channel_secret;
		}

		return {
			stateForChangingNumberToVerify: null,
			facebookPageToContactCustomer,
			lineModuleEnabled,
			state: (facebookPageToContactCustomer || lineModuleEnabled) ? this.STATE.AUTH_METHOD_SELECTION : this.STATE.PHONE_AUTH,
			name: '',
			countryCode: this.context.countryCode,
			phoneCallingCode: '',
			phoneNumber: '',
			email: '',
			passwordConfirmation: '',
			password: '',
			showInvalid: false,
			existingMember: null,
			smsCode: '',
			isLoading: false,
			loginMethod: '',
			isRequestingReset: false,
			isActionButtonShow: true,
			isLineAutoLoginFailed: false,
			lastCheckedPhone: '',
			showSmsContactInfo: false,
			continueWithoutSmsVerification: false,
		};
	},
	watch: {
		state (newValue, oldValue) {
			if (this.shouldShowPhoneInput && !this.countryCode) {
				this.presetCountryCode();
			}
			if (newValue === this.STATE.SMS_VERIFICATION) {
				this.stateForChangingNumberToVerify = oldValue;
			} else if (newValue === this.STATE.PHONE_AUTH) {
				this.focusToPhoneInput();
			}
		}
	},
	mounted() {
		if (this.state === this.STATE.PHONE_AUTH) {
			this.presetCountryCode();
		}

		this.afterLineLoggedIn();

		window.addEventListener("message", async (event) => {
			const eventName = event.data.event;
			switch(eventName) {
				case 'FB_AUTH_LOGGED_IN':
					this.isLoading = true;
					await this.getLoggedInCustomer();
					this.isLoading = false;
					this.loginMethod = 'fb';
					this.state = this.STATE.LOGGED_IN;
					this.trackLogin('facebook', true);
					this.$emit('logged-in');
					break;
				
				case 'FB_AUTH_FACEBOOK_ID_LOGGED':
					this.loginMethod = 'fb';
					this.state = this.STATE.FILL_CUSTOMER_DATA;
					break;

				case 'FB_AUTH_FAILED':
					this.promptAlert(
						this.$t('fb_auth_failed'),
					);
					this.state = this.STATE.AUTH_METHOD_SELECTION;
					this.loginMethod = 'fb';
					this.trackLogin('facebook', false);
					break;
			}
		}, false);

		this.focusToPhoneInput();
	},

	computed: {
		...mapGetters({
			customer: 'app/customer',
		}),
		rootDomain() {
			const hostComponents = window.location.host.split('.');
			hostComponents.shift(); // remove subdomain
			return hostComponents.join('.');
		},
		fbAuthIframeUrl() {
			return "//" + this.rootDomain + '/fb_auth?subdomain=' + this.provider.subdomain
		},
		finalLoginHint() {
			return this.loginHint || this.$t('customer_login_hint');
		},
		shouldShowPhoneInput() {
			const statesRequiredPhoneInput = [
				this.STATE.PHONE_AUTH,
				this.STATE.FILL_PASSWORD_FOR_RESET,
				this.STATE.FILL_PASSWORD_FOR_LOGIN,
				this.STATE.PASSWORD_RESET,
				this.STATE.FILL_CUSTOMER_DATA,
				this.SMS_VERIFICATION
			];
			return statesRequiredPhoneInput.indexOf(this.state) >= 0;
		},
		customerDataRequired() {
			return this.state === this.STATE.FILL_CUSTOMER_DATA;
		},
		passwordRequired() {
			return this.state === this.STATE.FILL_PASSWORD_FOR_RESET ||
				this.state === this.STATE.FILL_PASSWORD_FOR_LOGIN ||
				(this.state === this.STATE.FILL_CUSTOMER_DATA && this.loginMethod != 'fb' && this.loginMethod != 'line')
		},
		passwordConfirmationRequired() {
			return this.state === this.STATE.FILL_PASSWORD_FOR_RESET ||
				(this.state === this.STATE.FILL_CUSTOMER_DATA && this.loginMethod != 'fb' && this.loginMethod != 'line')
		},
		hasValidPassword() {
			return !!this.password && this.password.length >= 6;
		},
		hasValidPasswordConfirmation() {
			return !this.password || this.password === this.passwordConfirmation;
		},
		hasValidName() {
			return this.existingMember || this.customer || this.name.length >= 2;
		},
		hasValidPhoneCallingCode() {
			return this.existingMember
				|| this.customer
				|| (!!this.phoneCallingCode && !!this.countryCode);
		},
		hasValidPhoneNumber() {
			return this.existingMember
				|| this.customer
				|| (!!this.countryCode && isValidPhoneNumber(this.phoneNumber, this.countryCode?.toUpperCase()));
		},
		hasValidEmail() {
			return this.existingMember || (this.email && this.email.match(Constants.validator.email)) || this.hasExistingMemberData;
		},
	},

	methods: {
		...mapActions({
			getLoggedInCustomer: 'app/getLoggedInCustomer',
			logOutCustomer: 'app/logOutCustomer',
			verifyAndLogIn: 'app/verifyAndLogIn',
			login: 'app/login',
			storeAllDataToLocalStorage: 'cart/storeAllDataToLocalStorage',
		}),
		onShowSmsContactInfo() {
			this.showSmsContactInfo = true;
			this.isActionButtonShow = true;
		},
		confirm() {
			if (this.showSmsContactInfo) {
				this.onClose();
			} else {
				this.loginWithCode();
			}
		},
		setContinueWithoutSmsCodeThenLogin() {
			this.promptConfirm(this.$t('prompt_alert_continue_login_without_sms_verification_hint'),
				this.$t('general.confirm'),
				 () => {
					this.continueWithoutSmsVerification = true;
					this.loginWithCode();
				}
			); 
		},
		focusToPhoneInput() {
			this.$nextTick(() => {
				this.$refs['phone-input']?.$el?.querySelector('input')?.focus()
			});
		},
		requestResetPassword() {
			this.isRequestingReset = true;
			this.state = this.STATE.FILL_PASSWORD_FOR_RESET;
		},
		checkMemberByPhone() {
			const cb = () => {
				this.showInvalid = !this.hasValidPhoneNumber || !this.hasValidPhoneCallingCode;
				if (this.showInvalid) {
					return;
				}
				if (this.existingMember) {
					if (this.existingMember.password_set) {
						this.state = this.STATE.FILL_PASSWORD_FOR_LOGIN;
					} else {
						this.state = this.STATE.FILL_PASSWORD_FOR_RESET;
					}
				} else {
					this.state = this.STATE.FILL_CUSTOMER_DATA;
				}
			}
			this.checkExistingMemberByPhoneNumber(cb);
		},
		goToSmsVerification () {
			this.showInvalid = false;
			if (this.state === this.STATE.FILL_CUSTOMER_DATA) {
				const passwordRequired = this.loginMethod !== 'fb' && this.loginMethod != 'line';
				this.showInvalid = !this.hasValidPhoneNumber
					|| !this.hasValidPhoneCallingCode
					|| !this.hasValidEmail
					|| !this.hasValidName
					|| (passwordRequired && (!this.hasValidPassword || !this.hasValidPasswordConfirmation));
			} else if (this.state === this.STATE.FILL_PASSWORD_FOR_RESET) {
				this.showInvalid = !this.hasValidPhoneNumber
					|| !this.hasValidPhoneCallingCode
					|| !this.hasValidPassword
					|| !this.hasValidPasswordConfirmation;
				if (this.isRequestingReset) {
					this.trackForgetPassword();
				}
			}
			if (false === this.showInvalid) {
				this.state = this.STATE.SMS_VERIFICATION;
			}
		},
		async loginWithPassword() {
			this.showInvalid = !this.hasValidPhoneNumber || !this.hasValidPhoneCallingCode || !this.hasValidPassword;
			if (this.showInvalid) {
				return;
			}
			this.isLoading = true;
			try {
				await this.login({phoneNumber: this.phoneNumber, phoneCallingCode: this.phoneCallingCode, password: this.password});
				if(this.customer) {
					this.trackLogin('phone', true);
					this.$emit('logged-in');
				}
			} catch (t) {
				this.trackLogin('phone', false);
				const serverError = t.response?.data?.error || this.$t('auth_failed');
				this.promptAlert(serverError);
			} finally {
				this.isLoading = false;
			}
		},
		async loginWithCode() {
			const passwordRequired = this.loginMethod !== 'fb' && this.loginMethod != 'line';
			this.showInvalid = !this.smsCode
				|| !this.hasValidPhoneNumber
				|| !this.hasValidPhoneCallingCode
				|| !this.hasValidName
				|| !this.hasValidEmail
				|| (passwordRequired && (!this.hasValidPassword || !this.hasValidPasswordConfirmation));
			if (this.showInvalid) {
				return;
			}
			this.isLoading = true;
			try {
				await this.verifyAndLogIn({
					code: this.smsCode,
					phoneNumber: this.phoneNumber,
					phoneCallingCode: this.phoneCallingCode,
					name: this.name,
					email: this.email,
					password: this.password,
					continueWithoutSmsVerification: this.continueWithoutSmsVerification ? 1 : 0,
				});
				if(this.customer) {
					this.$emit('logged-in', this.loginMethod === 'line');
					this.trackVerifySms(true);
					this.trackLogin('phone', true);
				}
			} catch (e) {
				if (e.response && e.response.data.error) {
					this.promptAlert(e.response.data.error);
					if (e.response.data.error === this.$t('sms_not_verified')) {
						this.trackVerifySms(false);
					}
				} else {
					this.promptAlert(e.message);
				}
				this.trackLogin('phone', false);
			} finally {
				this.isLoading = false;
			}
		},
		presetCountryCode() {
			this.$nextTick(() => {
				if (this.$refs['phone-input']) {
					this.$refs['phone-input'].setCode('tw');
				}
			});
		},
		onPhoneNumberChange(phoneInputComponent) {
			this.phoneNumber = phoneInputComponent.phoneNumber;
			this.phoneCallingCode = new String( phoneInputComponent.availableData[phoneInputComponent.countryCode].dialCode);
			this.countryCode = phoneInputComponent.countryCode;
		},
		trackLogin(method, isSuccess) {
			window.tracker.track(() => new EventLogin(method, isSuccess));
		},
		trackForgetPassword() {
			window.tracker.track(() => new EventForgetPassword());
		},
		trackVerifySms(isSuccess) {
			window.tracker.track(() => new EventVerifySms('login', isSuccess));
		},
		async onLineLogin() {
			if (!this.lineModuleEnabled) {
				return;
			}
			await this.storeAllDataToLocalStorage();
			const state = Vue.dingGenerateLocalId();
			const finalCallbackURL = window.location.href;
			let queryString = new URLSearchParams({ final_redirect_uri: finalCallbackURL }).toString();
			let lineCallbackURL = `${window.location.origin}/line_auth/ok?${queryString}`;
			const payload = {
				response_type: 'code',
				client_id: this.provider.line_integration?.line_login_channel_id,
				redirect_uri: lineCallbackURL,
				state,
				bot_prompt: 'aggressive',
			};
			if (this.isLineAutoLoginFailed) {
				payload.disable_auto_login = true;
			}
			let data = new URLSearchParams(payload).toString();
			data = data + '&scope=profile%20openid';

			const link = `https://access.line.me/oauth2/v2.1/authorize?${data}`;
			window.location = link;
		},
		async afterLineLoggedIn() {
			if (!this.lineModuleEnabled) {
				return;
			}

			const result = Cookies.get('line_login_result');
			Cookies.remove('line_login_result', { path: '/', domain: this.rootDomain });

			const onError = () => {
				this.state = this.STATE.AUTH_METHOD_SELECTION;
				this.loginMethod = '';
				this.promptAlert(this.$t('line_login_failed'));
			};

			switch(result) {
				case 'LINE_AUTH_LOGGED_IN':
					this.isLoading = true;
					await this.getLoggedInCustomer();
					this.loginMethod = 'line';
					this.state = this.STATE.LOGGED_IN;
					this.isLoading = false;
					this.$emit('logged-in', this.loginMethod === 'line');
					break;

				case 'LINE_AUTH_LINE_ID_LOGGED':
					this.loginMethod = 'line';
					this.state = this.STATE.FILL_CUSTOMER_DATA;
					break;

				case 'LINE_AUTH_FAILED':
					onError();
					break;

				default:
			}
		},
		onClose() {
			this.$emit('close');
		},
		checkExistingMemberByPhoneNumber(onResult) {
			const fullPhoneNumber = this.phoneCallingCode + this.phoneNumber;
			if (this.hasValidPhoneNumber && (this.lastCheckedPhone !== fullPhoneNumber)) {
				const close = this.promptLoading(this.$t('getting_member_data'));
				this.existingMember = null;
				this.checkExistingMember(this.phoneCallingCode, this.phoneNumber, (result, error) => {
					close();
					if (!error && result.data) {
						this.name = "";
						this.email = "";
						this.$set(this, 'existingMember', result.data);
					} else {
						this.existingMember = null;
					}
					this.lastCheckedPhone = fullPhoneNumber;
					onResult && onResult();
				});
			} else {
				onResult && onResult();
			}
		},
		onChangePhoneNumber() {
			if (this.stateForChangingNumberToVerify === this.STATE.FILL_PASSWORD_FOR_RESET) {
				this.state = this.STATE.PHONE_AUTH;
			} else {
				this.state = this.stateForChangingNumberToVerify;
			}
			this.focusToPhoneInput();
		},
	},
}
</script>

<style lang="scss" scoped>
	/deep/ .intl-phone-input {
		width: 100%;
	}

	/deep/ .intl-phone-input input[type="text"] {
		border: solid 1px rgba(197,214,222,.7);
		box-shadow: none;
	}

	.login-box-container {
		z-index: 65535;
		width: 100%;
		max-width: 360px;
		margin: 0 auto;
		background: white;
		border-radius: 6px;
		padding: 16px 8px;
		position: relative;

		.top-control {
			text-align: right;
			padding-right: 12px;
			padding-left: 12px;
			display: flex;
			flex-direction: row;
		}

		.title {
			font-size: 1.5em;
			margin: 0.125em 0.25em 0.25em;
		}

		.login-hint {
			font-size: 0.9em;
			margin: 0.25em 2em 2em;
		}

		.login-button-wrapper {
			display: flex;
			flex-direction: column;
			align-items: center;
		}
	}

	.border-none {
		border: none;
	}

	.logout-link {
		display: block;
		margin-top: 16px;
	}

	.small-font {
		font-size: 0.9em;
	}

	label {
		margin-bottom: 0.05em;
		font-size: 0.8em;
		font-weight: bold;
	}

	.close-button {
		padding: 2px 4px;
		border-radius: 2px;
		cursor: pointer;
		display: inline-block;
		font-size: 0.9em;
		color: #666;

		&:hover {
			background: #eee;
		}

		i {
			position: relative;
			top: 2px;
		}

	}

	.logo img {
		display: block;
		margin: 12px auto 32px;
		max-width: 60px;
	}

	.btn-phone-auth {
		color: #444;
		background: white;
		border: 1px solid #444 !important;
		padding: 6px 16px;
		line-height: 24px;
		display: block;
		border-radius: 4px;
		user-select: none;
		width: 220px;

		&:hover {
			color: #444;
			background-color: #F9F9F9;;
			text-decoration: none;
			cursor: pointer;
		}
	}

	.btn-line-auth {
		color: #06C755;
		background: white;
		border: 1px solid #06C755 !important;
		padding: 6px 16px;
		line-height: 24px;
		display: block;
		border-radius: 4px;
		background-image: url(/img/line-chat.png);
		background-position: left;
		background-repeat: no-repeat;
		background-size: 24px;
		background-position-x: 8px;
		padding-left: 48px;
		user-select: none;
		width: 220px;

		&:hover {
			color: #06C755;
			background-color: #F9F9F9;;
			text-decoration: none;
			cursor: pointer;
		}
	}

</style>

<i18n>
{
	"en": {
		"getting_member_data": "Checking...",
		"please_enter_name": "Enter your name",
		"please_enter_email": "Enter your email",
		"fb_logged_in_greet": "Hi {name}! Please fill below information.",
		"not_you_click_to_logout": "Not you? Click here to log out.",
		"choose_other_method": "ᐸ Back",
		"customer_login": "Member Login",
		"customer_login_hint": "Log in to book faster and check your bookings!",
		"login": "Login",
		"login_via_phone": "Mobile SMS Login",
		"next_step": "Next",
		"login": "Login",
		"continue_login_without_sms_verification": "Skip verification",
		"continue_login_without_sms_verification_hint": "Some member functions will be unavailable",
		"prompt_alert_continue_login_without_sms_verification_hint": "If log in without verifying your number, your cccess of member functions will be limited.",
		"verify_sms": "Verify Number",
		"valid_email_required": "Please enter valid email",
		"valid_name_required": "Please enter valid name",
		"password": "Password",
		"password_hint": "Password should be at least 6 alpha-numberics.",
		"password_confirmation": "Password Confirmation",
		"password_confirmation_hint": "Please enter password again",
		"password_should_be_at_least_6_digits": "Password should be more than 6 alpha-numerics.",
		"password_confirmation_not_matched": "Password is not matched with confirmation.",
		"reset_password_required_hint": "You data found but not password set, please set your password.",
		"enter_your_name": "Your full name",
		"enter_your_email": "Your email for us to send notifications",
		"auth_failed": "Fail to log in",
		"request_reset_password_required_hint": "Please enter new password below to reset",
		"reset_password": "Reset Password",
		"sms_not_verified": "Fail to verify your number",
		"line_login_failed": "Fail to login through LINE",
		"line_login": "LINE Login",
		"confirmed": "Confirmed"
	},
	"zh": {
		"getting_member_data": "取得資料中...",
		"please_enter_name": "請輸入姓名",
		"please_enter_email": "請輸入 Email",
		"fb_logged_in_greet": "{name} 您好！請填寫下方聯絡資料，謝謝您。",
		"not_you_click_to_logout": "不是你嗎？請點此登出",
		"choose_other_method": "ᐸ 返回",
		"customer_login_hint": "歡迎註冊並登入會員，預約更快速，並可方便查看您的預約！",
		"customer_login": "會員登入",
		"login": "登入/註冊",
		"login_via_phone": "手機號碼登入",
		"next_step": "下一步",
		"login": "登入",
		"continue_login_without_sms_verification": "跳過驗證",
		"continue_login_without_sms_verification_hint": "部分會員功能將無法使用",
		"prompt_alert_continue_login_without_sms_verification_hint": "如不驗證手機號碼就進行登入，會員功能將有所限制。",
		"verify_sms": "驗證號碼",
		"valid_email_required": "請輸入您的 Email",
		"valid_name_required": "請輸入您的姓名",
		"password": "密碼",
		"password_confirmation": "密碼確認",
		"password_confirmation_hint": "請再輸入一次密碼",
		"password_hint": "密碼至少為六位英數",
		"password_should_be_at_least_6_digits": "密碼請輸入六位英數",
		"password_confirmation_not_matched": "密碼兩次輸入不符，請重新輸入",
		"reset_password_required_hint": "已找到您的資料，但您尚未設定過密碼，請設定密碼以繼續",
		"enter_your_name": "請輸入您的姓名",
		"enter_your_email": "請輸入聯繫用 Email 地址",
		"auth_failed": "無法登入，請稍後再試",
		"request_reset_password_required_hint": "請在下方輸入新密碼來重設您的登入密碼",
		"reset_password": "重設密碼",
		"sms_not_verified": "手機號碼驗證失敗",
		"line_login_failed": "LINE 登入失敗",
		"line_login": "LINE 登入",
		"confirmed": "我已確認"
	}
}
</i18n>
